import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { WorkLog } from "../../types/worklog.type";
import { api } from "../../api/api";
import {
  Button,
  DatePicker,
  Image,
  message,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { API } from "../../config/config";
import { renderTime } from "../../utils/renderTime";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function WorkLogs() {
  const { itemId, name } = useParams();
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [dateRange, setDateRange] = useState<[any, any] | undefined>(undefined);
  const [filter, setFilter] = useState<"overtime" | "underTime">();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  const handleExport = () => {
    const exportParams = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
    };
    downloadFile(
      `/export/work-logs/${itemId}`,
      "worklogs_data.xlsx",
      exportParams
    );
  };

  const fetchWorkLogs = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/worker/${itemId}/logs`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          filterType: filter,
        },
      });
      setWorkLogs(response.data.workLogs);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение смен.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkLogs();
  }, [page, pageSize, dateRange, filter]);

  const columns = [
    {
      title: "Начало смены",
      dataIndex: "startTime",
      key: "startTime",
    },
    {
      title: "Фото начала смены",
      key: "startSnapshot",
      dataIndex: "startSnapshot",
      render: (startSnapshot: string) => (
        <div>
          <Image
            key={startSnapshot}
            src={`${API}/${startSnapshot}`}
            width={50}
            height={50}
            preview={{ src: `${API}/${startSnapshot}` }}
            alt="Фото"
            loading="lazy"
            style={{
              cursor: "pointer",
              borderRadius: 4,
              border: "1px solid #ddd",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
          />
        </div>
      ),
    },
    {
      title: "Конец смены",
      dataIndex: "endTime",
      key: "endTime",
      render: (endTime: string) => (endTime ? endTime : "Еще не закончена"),
    },
    {
      title: "Фото конца смены",
      key: "endSnapshot",
      dataIndex: "endSnapshot",
      render: (endSnapshot: string) => {
        if (!endSnapshot) return "Еще не закончена";
        return (
          <div>
            <Image
              key={endSnapshot}
              src={`${API}/${endSnapshot}`}
              width={50}
              height={50}
              preview={{ src: `${API}/${endSnapshot}` }}
              alt="Фото"
              loading="lazy"
              style={{
                cursor: "pointer",
                borderRadius: 4,
                border: "1px solid #ddd",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Норма работы",
      dataIndex: "minuteNorm",
      key: "minuteNorm",
      render: (minuteNorm: number) => renderTime(minuteNorm),
    },
    {
      title: "Продолжительность смены",
      dataIndex: "workedMinutes",
      key: "workedMinutes",
      render: (workedMinutes: number) => renderTime(workedMinutes),
    },

    {
      title: "Не доработано",
      dataIndex: "underTime",
      key: "underTime",
      render: (underTime: number) => renderTime(underTime),
    },
    {
      title: "Переработано",
      dataIndex: "overtime",
      key: "overtime",
      render: (overtime: number) => renderTime(overtime),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Смены {name}</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={(dates) => setDateRange(dates ?? undefined)}
          placeholder={["Начало", "Конец"]}
          format={{ format: "DD.MM.YY" }}
        />
        <Select
          placeholder="Фильтр"
          onChange={(value) => setFilter(value)}
          allowClear
          style={{ width: 220 }}
        >
          <Option value="underTime">Недоработал</Option>
          <Option value="overtime">Переработал</Option>
        </Select>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={workLogs}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
}
